import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert, Input, Button, Form, FormGroup, Label } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const BankingReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Filter States
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [status, setStatus] = useState("");
  const [filterType, setFilterType] = useState("customerId");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 30;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    // Initial load hits the single BANKING_REPORT endpoint
    fetchBankingSummary(1, {});
  }, []);

  const fetchBankingSummary = async (page = 1, filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filters };
      params.page = page - 1; // Backend is 0-indexed
      params.size = rowsPerPage;

      // ✅ Hits single API: BANKING_REPORT
      const res = await axiosInstance.get(URL.BANKING_REPORT, { params });
      
      const resultData = Array.isArray(res.data) ? res.data : [];

      setData(resultData);
      setCurrentPage(page);
      setHasNextPage(resultData.length === rowsPerPage);
    } catch (err) {
      setError("Failed to fetch banking reporting data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (status) filters.status = status;
    if (filterType === "customerId" && customerId) filters.customerId = customerId;

    setActiveFilters(filters);
    fetchBankingSummary(1, filters);
  };

  const handleExcelExport = async () => {
    if (data.length === 0) return alert("No data available to export!");

    setExporting(true);
    try {
      const params = { ...activeFilters, export: true };
      const response = await axiosInstance.get(URL.BANKING_REPORT, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Banking_Report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed. Please check connectivity.");
    } finally {
      setExporting(false);
    }
  };

  const font10 = { fontSize: "10px" };
  const inputSmStyle = { fontSize: "11px", height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { fontSize: "11px", padding: "4px 10px", height: "30px", lineHeight: "1" };

  return (
    <div style={font10}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Filter Type</Label>
            <Input type="select" bsSize="sm" value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCustomerId(""); }}
              style={inputSmStyle}>
              <option value="customerId">Customer ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Customer ID</Label>
            <Input bsSize="sm" type="number" value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              style={inputSmStyle} placeholder="ID" />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Banking Status</Label>
            <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={inputSmStyle}>
              <option value="">All</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="PENDING">PENDING</option>
              <option value="REJECT">REJECT</option>
              <option value="EXPIRED">EXPIRED</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">From Date</Label>
            <Input type="date" bsSize="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">To Date</Label>
            <Input type="date" bsSize="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <div className="d-flex" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>Search</Button>
            <Button color="success" onClick={handleExcelExport} style={buttonSmStyle} disabled={exporting}>
              {exporting ? <Spinner size="sm" /> : "Excel"}
            </Button>
          </div>
        </div>
      </Form>

      {loading ? (
        <div className="text-center p-4"><Spinner size="sm" /> Loading Banking Data...</div>
      ) : error ? (
        <Alert color="danger" style={font10}>{error}</Alert>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <Table bordered striped hover responsive size="sm" className="mt-2 mb-0 text-nowrap">
              <thead className="thead-light">
                <tr>
                  <th>#</th>
                  <th>Id</th>
                  <th>Customer ID</th>
                  <th>Status</th>
                  <th>Create Date</th>
                  <th>Vue</th>
                  <th>Consent Handle</th>
                  <th>Account ID</th>
                  <th>Fintech Name</th>
                  <th>Account No</th>
                  <th>Name</th>
                  <th>Match Score</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Update Date</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{item.id || "N/A"}</td>
                      <td>{item.customer_id || "N/A"}</td>
                      <td>{item.status || "N/A"}</td>
                      <td>{item.create_date || "N/A"}</td>
                      <td>{item.vue || "N/A"}</td>
                      <td>{item.consent_handle || "N/A"}</td>
                      <td>{item.account_id || "N/A"}</td>
                      <td>{item.fintech_name || "N/A"}</td>
                      <td>{item.account_no || "N/A"}</td>
                      <td>{item.name || "N/A"}</td>
                      <td>{item.name_match_score || "N/A"}</td>
                      <td>{item.start_date || "N/A"}</td>
                      <td>{item.end_date || "N/A"}</td>
                      <td>{item.update_date || "N/A"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="15" className="text-center">No data found</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchBankingSummary(currentPage - 1, activeFilters)} 
                disabled={currentPage === 1}>Prev</Button>
              <span className="mx-2">Page {currentPage}</span>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchBankingSummary(currentPage + 1, activeFilters)} 
                disabled={!hasNextPage}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BankingReport;